import React, { useEffect, useState, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from '../../services/hooks/useLocalStorage';
import { cardLogin, pinLogin, enableMFPCardListener, getMFPCardInfo } from '../../services/api';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import jwtDecode from 'jwt-decode';
import { STC_ID, STRATUS_ID } from '../../services/constants';

import Loading from '../Loading';

import EmptyTargetBig from '../../img/empty-target.png';
import CardTarget from '../../img/card-target.png';
import CardHand from '../../img/card-in-hand.png';
import STCLogo from '../../img/small-body-logo-stc.png';
import STCLogoBig from '../../img/body-logo-stc.png';
import StratusLogo from '../../img/small-body-logo-stratus.png';
import StratusLogoBig from '../../img/body-logo-stratus.png';
import Swoosh from '../../img/purple-swoosh.png';
import SwooshBig from '../../img/purple-swoosh-big.png';
import Caution from '../../img/not-found.png';
import CautionSmall from '../../img/not-found-small.png';
import Backup from '../../img/backup.png';
import BackupDisabled from '../../img/backup-disabled.png';
import { Button } from '@mui/material';

const useStyles = makeStyles({
    button_provider: {
        width: '270px',
        height: '36px',
    },
    button_provider_span: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    buttons: {
        overflow: 'auto',
        maxHeight: 'calc(100vh - 134px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    button_container: {
        width: '70%',
    },
    card_hand: {
        position: 'absolute',
        left: '90px',
        top: '160px',
    },
    card_association_buttons: {
        display: 'flex',
        flexDirection: 'column',
    },
    caution: {
        marginBottom: '60px',
        marginRight: '42px',
        '@media (max-width: 800px)': {
            marginBottom: '57px',
            marginRight: '31px',
        },
    },
    container: {
        display: 'grid',
        gridTemplateRows: '49px 486px',
        gridTemplateColumns: '416px auto',
        gridTemplateAreas: '"header header" "card content"',
        overflow: 'hidden',
        '@media (max-width: 800px)': {
            gridTemplateColumns: '308px auto',
        },
    },
    container_error: {
        display: 'grid',
        gridTemplateRows: '49px 486px',
        gridTemplateColumns: '416px auto',
        gridTemplateAreas: '"header header" "card content"',
        overflow: 'hidden',
        '@media (max-width: 800px)': {
            gridTemplateColumns: '308px auto',
        },
    },
    input_label: {
        color: 'white',
        fontSize: '19px',
        fontFamily: 'sans-serif',
        width: '70%',
    },
    label: {
        color: 'white',
        fontSize: '21px',
        fontFamily: 'sans-serif'
    },
    label_error: {
        color: 'white',
        fontSize: '18px',
        fontFamily: 'sans-serif'
    },
    label_error_header: {
        fontSize: '21px',
        fontWeight: 'bold',
    },
    left: {
        display: 'flex',
        alignItems: 'center',
        height: 'calc(100vh - 66px)',
    },
    left_logo_error: {
        display: 'flex',
        height: 'calc(100vh - 66px)',
        alignItems: 'center',
    },
    logo: {
        zIndex: '1',
        marginRight: '30px',
        maxWidth: '450px',
        '@media (max-width: 800px)': {
            marginRight: '0',
            maxWidth: '378px',
        },
    },
    pin_buttons: {
        backgroundColor: 'white',
        border: '1px solid white',
        placeContent: 'center',
        textAlign: 'center',
        borderRadius: '50px',
        fontWeight: 'bold',
        fontSize: '32px',
        cursor: 'pointer',
        outline: 'none',
        transition: 'none',
        color: '#603FA5',
    },
    pin_pad: {
        display: 'grid',
        gridTemplateColumns: '59px 59px 59px',
        gridTemplateRows: '21px 38px 59px 59px 59px 59px',
        gridTemplateAreas: '"inst inst inst" "cnt cnt cnt" "p1 p2 p3" "p4 p5 p6" "p7 p8 p9" " . p0 px"',
        gridRowGap: '10px',
        gridColumnGap: '10px',
        position: 'absolute',
        top: '52px',
        left: '80px',
        color: '#603FA5',
        '@media (max-width: 800px)': {
            top: '70px',
            left: '40px',
            gridTemplateColumns: '49px 49px 49px',
            gridTemplateRows: '16px 20px 49px 49px 49px 49px',
            gridRowGap: '8px',
        },
    },
    pin_inst: {
        fontSize: '21px',
        gridArea: 'inst',
        placeContent: 'center',
        textAlign: 'center',
        '@media (max-width: 800px)': {
            fontSize: '15px',
        },
    },
    pin_pc: {
        width: '10px',
        height: '10px',
        borderRadius: '10px',
        border: '2px solid #603FA5',
        '@media (max-width: 800px)': {
            width: '7px',
            height: '7px',
            borderRadius: '7px'
        },
    },
    pin_progress: {
        gridArea: 'cnt',
        placeContent: 'center',
        textAlign: 'center',
        display: 'grid',
        gridTemplateColumns: '10px 10px 10px 10px 10px 10px',
        gridColumnGap: '20px',
        '@media (max-width: 800px)': {
            gridColumnGap: '15px',
        },
    },
    provider_container: {
        position: 'absolute',
        right: '40px',
        minHeight: '486px',
        display: 'grid',
        justifyItems: 'center',
        gridTemplateRows: '120px'
    },
    provider_select: {
        color: '#603FA5',
        display: 'flex',
        justifyItems: 'center',
        alignItems: 'flex-end',
        marginBottom: '20px',
    },
    right: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '66px',
        gridTemplateRows: '146px 40px 32px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center'
    },
    right_error: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '66px',
        gridTemplateRows: '146px 56px 64px 64px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center',
    },
    right_error_with_header: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '66px',
        gridTemplateRows: '146px 40px 64px 64px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center',
    },
    right_text: {
        '@media (max-width: 860px)': {
            fontSize: '14px',
        },
        '&::first-letter': {
            textTransform: 'capitalize',
        },
    },
    swoosh: {
        backgroundImage: `url(${Swoosh})`,
        height: "486px",
        width: "78px",
        position: 'absolute',
        top: '-7px',
        left: '340px',
        '@media (max-width: 800px)': {
            left: '232px',
        },
    },
    swoosh_error: {
        backgroundImage: `url(${Swoosh})`,
        height: "486px",
        width: "78px",
        position: 'absolute',
        top: '-7px',
        left: '340px',
        '@media (max-width: 800px)': {
            left: '340px',
        },
    },
    swoosh_big: {
        backgroundImage: `url(${SwooshBig})`,
        height: "535px",
        width: "74px",
        position: 'absolute',
        top: '1px',
        left: '344px',
    },
    target: {
        position: 'absolute',
        zIndex: '-1',
        right: '0',
        top: '0',
        transform: 'scaleX(-1)',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    },
    target_left: {
        backgroundImage: `url(${EmptyTargetBig})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '-1',
        width: '404px',
        height: '450px',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    },
    target_card_left: {
        backgroundImage: `url(${CardTarget})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        zIndex: '-1',
        width: '404px',
        height: '450px',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    }
})

const PinAndCardAuth = (props) => {
    const [stratus] = useLocalStorage("stratus", false);
    const [loading, setLoading] = useState(true);
    const [errorHeader, setErrorHeader] = useState('');
    const [associateCard, setAssociateCard] = useState(false);
    const [lastScannedCardId, setLastScannedCardId] = useState(undefined);
    const [error, setError] = useState('');
    const [pin, setPin] = useState('');
    const PIN_LIMIT = 6;
    const classes = useStyles();
    const ns = stratus ? 'stratus' : 'scantrip';
    const { t } = useTranslation(ns);
    const { promiseInProgress } = usePromiseTracker({ delay: 0 })

    const logo = window.innerWidth > 800 ? (stratus ? StratusLogoBig : STCLogoBig) : (stratus ? StratusLogo : STCLogo);
    const caution = window.innerWidth > 800 ? Caution : CautionSmall;

    const handlePinInput = (n) => {
        setPin(prevPin => {
            if (prevPin.length === PIN_LIMIT) {
                return prevPin
            }
            return `${prevPin}${n}`
        })
    }

    const handlePinBackspace = () => {
        setPin(prevPin => {
            return prevPin.slice(0, prevPin.length - 1)
        })
    }

    useEffect(() => {
        const keyHandler = (event) => {
            if (event.keyCode >= 48 && event.keyCode <= 57) {
                handlePinInput(event.key)
            }

            if (event.keyCode === 8 || event.keyCode === 58) {
                // Backspace or # (mfp keypad same position as backpace icon)
                // triggers backspace
                handlePinBackspace()
            }
        }
        window.addEventListener("keyup", keyHandler)

        return () => {
            window.removeEventListener("keyup", keyHandler)
        }
    }, [])

    useEffect(() => {
        let appId = STC_ID
        if (stratus) {
            appId = STRATUS_ID
        }
        const enableCard = async () => {
            const cardOk = await trackPromise(enableMFPCardListener(appId))
            if (!cardOk) {
                // TODO Error handling for this case
                console.error('NO CARD')
            }
        }

        const handleCardLogin = async () => {
            try {
                // Get card ID
                const cardData = await getMFPCardInfo(appId);
                if (cardData.error) {
                    setError(t('cognito:card_swipe_error'))
                    return
                }
                const cardId = cardData.id;
                setLastScannedCardId(cardId);
                const tenant = props.tenant;
                const resp = await cardLogin(cardId, tenant, stratus ? STRATUS_ID : STC_ID);
                if (!resp.ok) {
                    if (resp.status === 404) {
                        setErrorHeader(t('cognito:card:not_found'))
                        setError(t('cognito:card:login_to_associate_card'))
                        setAssociateCard(true)
                        return
                    }
                    console.log(resp)
                    setError(t('cognito:credentials_invalid'))
                    return
                }
                const idToken = resp.body.idToken;
                const accessToken = resp.body.accessToken;
                const refreshToken = resp.body.refreshToken;
                const decodedIdToken = jwtDecode(idToken);
                const decodedAccessToken = jwtDecode(accessToken);
                const user = decodedIdToken.email || decodedAccessToken.username;
                props.setters.setTenant({ id: tenant.id, name: tenant.name, plan: tenant.plan, region: tenant.region, slug: tenant.slug });
                props.setters.setIdToken(idToken);
                props.setters.setAccessToken(accessToken);
                props.setters.setRefreshToken(refreshToken);
                props.setters.setClient(tenant.customClient);
                props.setters.setUser(user);
            } catch (e) {
                console.error(e)
                setError(t('cognito:unknown_card_error'))
                setLoading(false)
            }
        }

        const cardHandler = (event) => {
            if (!props.cardEnabled) {
                return
            }
            if (event.keyCode === 257 || event.keyCode === 13) {
                trackPromise(handleCardLogin())
            }
        }

        if (props.cardEnabled) {
            enableCard()
            window.addEventListener("keydown", cardHandler)
            setLoading(false)
            return () => {
                window.removeEventListener("keydown", cardHandler)
            }
        }
        setLoading(false)
        // eslint-disable-next-line
    }, [props.cardEnabled, stratus])

    const handlePinLogin = async (pin) => {
        const tenant = props.tenant;
        const resp = await pinLogin(pin, tenant, stratus ? STRATUS_ID : STC_ID)
        if (!resp.ok) {
            setPin('');
            console.log(resp)
            setError(t('cognito:credentials_invalid'))
            return
        }
        const idToken = resp.body.idToken;
        const accessToken = resp.body.accessToken;
        const refreshToken = resp.body.refreshToken;
        const decodedIdToken = jwtDecode(idToken);
        const decodedAccessToken = jwtDecode(accessToken);
        const user = decodedIdToken.email || decodedAccessToken.username
        props.setters.setTenant({ id: tenant.id, name: tenant.name, plan: tenant.plan, region: tenant.region, slug: tenant.slug });
        props.setters.setIdToken(idToken);
        props.setters.setAccessToken(accessToken);
        props.setters.setRefreshToken(refreshToken);
        props.setters.setClient(tenant.customClient);
        props.setters.setUser(user);
    }

    const cbHandlePinLogin = useCallback(handlePinLogin, [props, t, stratus])

    useEffect(() => {
        for (let i = 0; i < pin.length; i++) {
            const pip = document.getElementById(`pc-${i}`)
            if (pip) {
                pip.style['background-color'] = '#603FA5'
            }
        }
        for (let i = pin.length; i < PIN_LIMIT; i++) {
            const pip = document.getElementById(`pc-${i}`)
            if (pip) {
                pip.style['background-color'] = ''
            }
        }
        if (pin.length === PIN_LIMIT) {
            cbHandlePinLogin(pin)
        }
        // eslint-disable-next-line
    }, [pin])

    const setPinSkip = () => {
        props.continue(true)
    }

    const generateLeftSide = () => {
        const pinEnabled = props.pinEnabled;
        if (!pinEnabled) {
            return (
                <div id="left" className={classes.left}>
                    <div className={classes.target_card_left}>
                        <img src={CardHand} className={classes.card_hand} alt="Hand holding a card" />
                    </div>
                </div>
            )
        }
        const pinProgress = [0, 1, 2, 3, 4, 5].map(p => <div key={`pc-${p}`} id={`pc-${p}`} className={classes.pin_pc}></div>)
        const pinNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map(p => (
            <Button
                key={`pin-${p}`}
                className={classes.pin_buttons}
                id={`pin-${p}`}
                onClick={() => handlePinInput(p)}
                sx={{
                    backgroundColor: 'white',
                    border: '1px solid white',
                    placeContent: 'center',
                    textAlign: 'center',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    fontSize: '32px',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'none',
                    color: '#603FA5',
                    gridArea: `p${p}`,
                    '@media (max-width: 800px)': {
                        fontSize: '24px',
                        width: '50px',
                        minWidth: '50px',
                        height: '50px',
                    }
                }}
            >
                {p}
            </Button>
        )
        )
        return (
            <div id="left" className={classes.left}>
                <div className={classes.target_left} />
                <div className={classes.pin_pad}>
                    <div className={classes.pin_inst}>{t('cognito:pin:enter')}</div>
                    <div className={classes.pin_progress} id="pin-progress">
                        {pinProgress}
                    </div>
                    {pinNumbers}
                    <Button
                        id="pin-x"
                        onClick={handlePinBackspace}
                        disabled={pin.length === 0}
                        sx={{
                            borderRadius: '0px',
                            border: '0',
                            backgroundImage: `url(${pin.length === 0 ? BackupDisabled : Backup})`,
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            gridArea: 'px',
                        }}
                    ></Button>
                </div>
            </div>
        )
    }

    const generateRightSide = () => {
        return (
            <div id="right" className={classes.right}>
                <div id="swoosh" className={window.screen.width > 1000 ? classes.swoosh_big : classes.swoosh}></div>
                <img src={logo} alt="Dispatcher ScanTrip Cloud" className={classes.logo} />
                <div>
                    <div className={classes.right_text}>
                        {props.pinEnabled ? <b style={{ margin: '0 4px' }}>{t('cognito:or')}</b> : <></>}
                        {props.cardEnabled ? t('cognito:card:tap_lower') : t('cognito:tap_lower')}
                    </div>
                </div>
                <Button
                    variant='contained'
                    disableElevation
                    onClick={() => setPinSkip()}
                    sx={{
                        width: "144px",
                        height: "36px",
                        margin: "16px 32px 32px 32px",
                        backgroundColor: "#272261",
                        textTransform: 'none',
                        '&:active': {
                            backgroundColor: '#272261',
                        },
                        '&:focus': {
                            backgroundColor: '#272261',
                        },
                        '&:hover': {
                            backgroundColor: '#272261',
                        },
                    }}
                >
                    {t('Continue')}
                </Button>
            </div>
        )
    }

    if (error) {
        return (
            <div className={classes.container_error}>
                <div id="left-logo" className={classes.left_logo_error}>
                    <div className={classes.target_left}>
                        <img src={caution} className={classes.caution} alt="Caution" />
                    </div>
                    <div id="swoosh" className={window.screen.width > 1000 ? classes.swoosh_big : classes.swoosh}></div>
                </div>
                <div id="right-action" className={errorHeader ? classes.right_error_with_header : classes.right_error}>
                    <img src={logo} alt="Dispatcher ScanTrip Cloud" className={classes.logo} />
                    {errorHeader ? <div id="error-header-text" className={classes.label_error_header}>{errorHeader}</div> : <></>}
                    <div id="error-text" className={classes.label_error}>{error}</div>
                    {associateCard ?
                        <>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    props.setters.setLastScannedCardId(lastScannedCardId)
                                    props.continue(true)
                                }}
                                sx={{
                                    backgroundColor: '#272261',
                                    width: '144px',
                                    height: '36px',
                                    float: 'right',
                                    textTransform: 'none',
                                    '&:active': {
                                        backgroundColor: '#272261',
                                    },
                                    '&:focus': {
                                        backgroundColor: '#272261',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#272261',
                                    },
                                }}
                            >
                                {t('Login')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setPin('')
                                    setError('')
                                    setErrorHeader('')
                                    setAssociateCard(false)
                                }}
                                sx={{
                                    color: 'white',
                                    width: '144px',
                                    height: '36px',
                                    float: 'right',
                                    textTransform: 'none',
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                        </> :
                        <Button
                            variant="contained"
                            onClick={() => {
                                setPin('')
                                setError('')
                            }}
                            sx={{
                                backgroundColor: '#272261',
                                width: '144px',
                                height: '36px',
                                float: 'right',
                                textTransform: 'none',
                                '&:active': {
                                    backgroundColor: '#272261',
                                },
                                '&:focus': {
                                    backgroundColor: '#272261',
                                },
                                '&:hover': {
                                    backgroundColor: '#272261',
                                },
                            }}
                        >
                            {t('Continue')}
                        </Button>
                    }
                </div>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            {props.loading || loading || promiseInProgress ? <Loading /> : <></>}
            {generateLeftSide()}
            {generateRightSide()}
        </div>
    )
}

export default PinAndCardAuth